/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {register} from "./requestSender";
import {removePageChanger} from "./pageChanger";
import {ProductSelectionClause, ProductSelectionClauseType} from "./types";

export const NO_REPLY_FLEX_KEY = 'use_no_reply_tracking';
export const ACCOUNT_PROGRESS_FLEX_KEY = 'account_setup_progress';
export const VAT_BETA_FLEX_KEY = 'vat_declaration_beta';
export const SELECTED_ACCOUNT_KEY = 'bolmate-selected-account';
export const SENDING_HISTORY_MAILS_KEY = 'sending_history_mails';
export const SURVEY_CHOICES_KEY = 'survey_choices';
export const COLOR_BLUE_BOLMATE = '#2568EF';
export const COLOR_GRAY_8 = '#D5D5D5';
export const ISO_DATE_FORMAT = 'YYYY-MM-DD';
export const DUTCH_DATE_FORMAT = 'DD-MM-YYYY';
export const DUTCH_DATE_TIME_FORMAT = `${DUTCH_DATE_FORMAT} HH:mm`;
export const BASIC_MESSAGES_KEY = 'basic_messages';
export const MONTH_LABELS = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september',
    'oktober', 'november', 'december'];
export const MULTI_LOGIN_BETA_KEY = 'multi-login-beta';
export const LAST_REVIEW_FLEX_KEY = 'review_feed_last_seen';
export const DEFAULT_PRODUCT_SELECTION: ProductSelectionClauseType = {clause_type: 'include', selection_type: 'all'};

export const REGISTER_PATH = 'registreren';
export const REGISTER_ROUTE = `/${REGISTER_PATH}`;
export const LOGIN_PATH = 'inloggen';
export const LOGIN_ROUTE = `/${LOGIN_PATH}`;
export const DEMO_PATH = 'demo';
export const DEMO_ROUTE = `/${DEMO_PATH}`;
export const FORGOT_PASSWORD_PATH = 'wachtwoord-vergeten';
export const FORGOT_PASSWORD_ROUTE = `/${FORGOT_PASSWORD_PATH}`;
export const RESET_PASSWORD_PATH = 'wachtwoord-resetten/:code';
export const RESET_PASSWORD_ROUTE = (code: string) => `/${RESET_PASSWORD_PATH}/${code}`;
export const TWO_FACTOR_RESET_PATH = '2fa-reset/:token';
export const TWO_FACTOR_RESET_ROUTE = (token: string) => `/${TWO_FACTOR_RESET_PATH}/${token}`;
export const PRICE_CALCULATOR_PATH = 'prijs-calculator';
export const PRICE_CALCULATOR_ROUTE = `/${PRICE_CALCULATOR_PATH}`;
export const MAINTENANCE_PATH = 'onderhoud';
export const MAINTENANCE_ROUTE = `/${MAINTENANCE_PATH}`;
export const INVOICES_PATH = 'facturen';
export const INVOICES_ROUTE = `/${INVOICES_PATH}`;
export const ORDERS_PATH = 'orders';
export const ORDERS_ROUTE = `${INVOICES_ROUTE}/${ORDERS_PATH}`;
export const INVOICE_REQUESTS_PATH = 'aanvragen';
export const INVOICE_REQUESTS_ROUTE = `${INVOICES_ROUTE}/${INVOICE_REQUESTS_PATH}`;
export const INVOICE_DETAILS_PATH = 'factuur-details';
export const INVOICE_DETAILS_ROUTE = `/${INVOICE_DETAILS_PATH}`;
export const EDIT_INVOICE_PATH = 'factuur-wijzigen';
export const EDIT_INVOICE_ROUTE = `/${EDIT_INVOICE_PATH}`;
export const PDF_LAYOUT_PATH = 'factuur-layout';
export const PDF_LAYOUT_ROUTE = `/${PDF_LAYOUT_PATH}`;
export const CONNECTION_COMPLETE_PATH = 'koppeling-geslaagd';
export const CONNECTION_COMPLETE_ROUTE = `/${CONNECTION_COMPLETE_PATH}`;
export const REVIEW_ANALYTICS_PATH = 'review-analytics';
export const REVIEW_ANALYTICS_ROUTE = `/${REVIEW_ANALYTICS_PATH}`;
export const PRODUCT_REVIEWS_PATH = 'product-reviews/:ean';
export const PRODUCT_REVIEWS_ROUTE = (ean: string) => `/${PRODUCT_REVIEWS_PATH}/${ean}`;
export const SEARCH_TRENDS_PATH = 'zoek-trends';
export const SEARCH_VOLUMES_PATH = 'zoekvolume';
export const SEARCH_VOLUMES_ROUTE = `/${SEARCH_VOLUMES_PATH}`;
export const SALES_DASHBOARD_PATH = 'sales-dashboard';
export const SALES_DASHBOARD_ROUTE = `/${SALES_DASHBOARD_PATH}`;
export const SETTINGS_PATH = 'instellingen';
export const SETTINGS_ROUTE = `/${SETTINGS_PATH}`;
export const COMMON_SETTINGS_PATH = 'algemeen';
export const COMMON_SETTINGS_ROUTE = `${SETTINGS_ROUTE}/${COMMON_SETTINGS_PATH}`;
export const ACCESS_SETTINGS_PATH = 'gebruikers';
export const ACCESS_SETTINGS_ROUTE = `${SETTINGS_ROUTE}/${ACCESS_SETTINGS_PATH}`;
export const SECURITY_SETTINGS_PATH = 'beveiliging';
export const SECURITY_SETTINGS_ROUTE = `${SETTINGS_ROUTE}/${SECURITY_SETTINGS_PATH}`;
export const API_SETTINGS_PATH = 'koppeling';
export const API_SETTINGS_ROUTE = `${SETTINGS_ROUTE}/${API_SETTINGS_PATH}`;
export const INVOICE_SETTINGS_PATH = 'factuur-instellingen';
export const INVOICE_SETTINGS_ROUTE = `${SETTINGS_ROUTE}/${INVOICE_SETTINGS_PATH}`;
export const PRODUCT_SETTINGS_PATH = 'producten';
export const PRODUCT_SETTINGS_ROUTE = `${SETTINGS_ROUTE}/${PRODUCT_SETTINGS_PATH}`;
export const SUBSCRIPTION_PATH = 'abonnement';
export const SUBSCRIPTION_ROUTE = `${SETTINGS_ROUTE}/${SUBSCRIPTION_PATH}`;
export const EXPORT_PATH = 'exporteren';
export const EXPORT_ROUTE = `${SETTINGS_ROUTE}/${EXPORT_PATH}`;
export const EMAIL_SETTINGS_PATH = 'emails';
export const EMAIL_SETTINGS_ROUTE = `${SETTINGS_ROUTE}/${EMAIL_SETTINGS_PATH}`;
export const SETUP_ACCOUNT_PATH = 'account-instellen';
export const SETUP_ACCOUNT_ROUTE = `/${SETUP_ACCOUNT_PATH}`;
export const CREDIT_DATA_PATH = 'credit-data';
export const CREDIT_DATA_ROUTE = `/${CREDIT_DATA_PATH}`;
export const IDEAL_PATH = 'ideal';
export const IDEAL_ROUTE = `/${IDEAL_PATH}`;
export const IDEAL_COMPLETE_PATH = 'ideal-complete';
export const IDEAL_COMPLETE_ROUTE = `/${IDEAL_COMPLETE_PATH}`;
export const EMAILS_PATH = 'emails';
export const EMAIL_SEQUENCE_PATH = 'email-volgorde';
export const EMAILS_ROUTE = `/${EMAILS_PATH}`;
export const EMAIL_TEMPLATE_PATH = 'template-editor';
export const EMAIL_TEMPLATE_ROUTE = `/${EMAIL_TEMPLATE_PATH}`;
export const RETURNS_PATH = 'retouren';
export const RETURNS_ROUTE = `/${RETURNS_PATH}`;
export const VAT_DECLARATION_PATH = 'btw-aangifte';
export const VAT_DECLARATION_ROUTE = `/${VAT_DECLARATION_PATH}`;
export const UNSUBSCRIBE_MONTHLY_PATH = 'uitschrijven-maandelijkse-link/:userId';
export const UNSUBSCRIBE_MONTHLY_ROUTE = (userID: string) => `/${UNSUBSCRIBE_MONTHLY_PATH}/${userID}`;
export const ADMIN_PATH = 'admin';
export const ADMIN_ROUTE = `/${ADMIN_PATH}`;
export const ADMIN_USER_PATH = 'gebruiker-details';
export const ADMIN_USER_ROUTE = `${ADMIN_ROUTE}/${ADMIN_USER_PATH}`;
export const ADMIN_ACCOUNT_PATH = 'account-details';
export const ADMIN_ACCOUNT_ROUTE = `${ADMIN_ROUTE}/${ADMIN_ACCOUNT_PATH}`;
export const ADMIN_THREADS_PATH = 'threads';
export const ADMIN_THREADS_ROUTE = `${ADMIN_ROUTE}/${ADMIN_THREADS_PATH}`;
export const ADMIN_USERS_PATH = 'gebruikers';
export const ADMIN_USERS_ROUTE = `${ADMIN_ROUTE}/${ADMIN_USERS_PATH}`;
export const ADMIN_SERVICES_PATH = 'services';
export const ADMIN_SERVICES_ROUTE = `${ADMIN_ROUTE}/${ADMIN_SERVICES_PATH}`;
export const ADMIN_IMPORT_PATH = 'historie-import';
export const ADMIN_IMPORT_ROUTE = `${ADMIN_ROUTE}/${ADMIN_IMPORT_PATH}`;
export const ADMIN_EMAILS_PATH = 'email-verzending';
export const ADMIN_EMAILS_ROUTE = `${ADMIN_ROUTE}/${ADMIN_EMAILS_PATH}`;
export const ADMIN_PAYMENTS_PATH = 'mislukte-betalingen';
export const ADMIN_PAYMENTS_ROUTE = `${ADMIN_ROUTE}/${ADMIN_PAYMENTS_PATH}`;
export const ADMIN_USERS_SELECT_PATH = 'gebruikers-selecteren';
export const ADMIN_USERS_SELECT_ROUTE = `${ADMIN_ROUTE}/${ADMIN_USERS_SELECT_PATH}`;
export const ADMIN_DEFAULT_TEMPLATES_PATH = 'admin-default-templates';
export const ADMIN_DEFAULT_TEMPLATES_ROUTE = `${ADMIN_ROUTE}/${ADMIN_DEFAULT_TEMPLATES_PATH}`;
export const ADMIN_TEMPLATE_EDITOR_PATH = 'admin-template-editor';
export const ADMIN_TEMPLATE_EDITOR_ROUTE = (templateID: string | null) => `${ADMIN_ROUTE}/${ADMIN_TEMPLATE_EDITOR_PATH}?template=${templateID ?? ""}`;
export const OAUTH_PATH = 'oauth/bol';
export const OAUTH_ROUTE = `/${OAUTH_PATH}`;
export const REFER_A_FRIEND_PATH = 'refer-a-friend';
export const REFER_A_FRIEND_ROUTE = `/${REFER_A_FRIEND_PATH}`;
export const NOTIFICATIONS_PATH = 'notificaties';
export const NOTIFICATIONS_ROUTE = `/${NOTIFICATIONS_PATH}`;
export const PEPPOL_INVOICES_PATH = 'peppol';
export const PEPPOL_INVOICES_ROUTE = `${INVOICES_ROUTE}/${PEPPOL_INVOICES_PATH}`;
export const ADMIN_PLATFORM_USAGE_PATH = 'platformgebruik';
export const ADMIN_PLATFORM_USAGE_ROUTE = `${ADMIN_ROUTE}/${ADMIN_PLATFORM_USAGE_PATH}`;
